import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import dateformat from 'dateformat'
import { includes } from 'lodash'
import EmailCallout, { EmailTypes } from 'components/email-callout'
import ImageWithPlaceholderColor from 'components/image-with-placeholder-color'
import Loader from 'components/loader'
import Pinnable from 'shared/pinnable'
import ShareButtons from 'shared/share-buttons'
import {
  babylistUrl,
  helloBabyPostPath,
  healthAdvisoryBoardPath,
  landingPagePath,
} from 'lib/urls'
import { EMAIL_USER_PREGNANCY, EMAIL_USER_NEWSLETTER } from 'shared/constants'
import Link from 'components/link'
import { track, useTracking } from 'lib/analytics'
import TopicList from '../topic-list'
import Author from '../author'
import AuthorByline from '../AuthorByline'
import PostCardRow from '../post-card-row'
import PostLink from '../post-link'
import css from './post-view.scss'
import PostNavBar from './post-nav-bar'
import PostSocialButtons from './post-social-buttons'
import { SectionHeading } from '../../../components/headings'
import FTCDisclosureTag from '../../../components/FTCDisclosureTag'
import SignUpBanner from './SignUpBanner'
import HelloBabyMeta from '../HelloBabyMeta'

const READ_LANGUAGE_LABEL: Record<'es-US' | 'en-US', string> = {
  'es-US': 'En Español',
  'en-US': 'In English',
}

interface Topic {
  name: string
  slug: string
}

interface Meta {
  pinterestImageUrl?: string
  socialDescription?: string
  socialImageUrl?: string
  socialTitle?: string
  socialUrl?: string
  seoDescription?: string
  seoTitle?: string
  topics: Topic[]
}

interface Post {
  author?: string
  authors?: { id: number }[]
  body: string
  featuredImage?: {
    attribution?: string
    attributionUrl?: string
    description?: string
    url: string
  }
  id?: string
  locales: { locale: string; slug: string }[]
  meta: Meta
  publishedAt: string
  revision: number
  slug: string
  socialShareUrl?: string
  teaser: string
  title: string
  type: string
  videoId?: string
}

interface FeaturedAssetProps {
  post: Post
  assetClasses: string
  minimalView: boolean
}

interface ContextProps {
  minimalView: boolean
}

const FeaturedAsset: React.FC<FeaturedAssetProps> = ({
  post,
  assetClasses,
  minimalView,
}) => {
  if (post.videoId) {
    return (
      <div className="breakout-right">
        <div
          className={classnames(
            'embed-responsive embed-responsive-16by9',
            assetClasses
          )}
        >
          <iframe
            allowFullScreen
            className="embed-responsive-item"
            src={`https://www.youtube.com/embed/${post.videoId}`}
            title="Babylist Video"
          />
        </div>
      </div>
    )
  }
  const image = (
    <ImageWithPlaceholderColor
      alt={post.featuredImage?.description}
      aspectHeight={2}
      aspectWidth={3}
      className=""
      src={post.featuredImage?.url}
    />
  )
  return (
    <div className={assetClasses}>
      {minimalView ? (
        <>{image}</>
      ) : (
        <Pinnable
          buttonSize="large"
          description={post.meta.socialDescription || ''}
          media={post.featuredImage?.url || ''}
          url={post.socialShareUrl || ''}
        >
          {image}
        </Pinnable>
      )}

      {post.featuredImage?.attribution ? (
        <div className="h7 mts post-featured-image-attribution">
          {`Photo by `}
          {post.featuredImage?.attributionUrl ? (
            <a
              href={post.featuredImage?.attributionUrl}
              rel="noreferrer"
              target="_blank"
            >
              {post.featuredImage?.attribution}
            </a>
          ) : (
            post.featuredImage?.attribution
          )}
        </div>
      ) : null}
    </div>
  )
}

interface LanguageSelectorProps {
  locales?: Array<{ locale: string; slug: string }>
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ locales }) => (
  <div className={classnames(css.languages)}>
    {locales?.map((l) => {
      const text = READ_LANGUAGE_LABEL[l.locale as 'es-US' | 'en-US']
      return text ? (
        <i key={l.slug}>
          <Link className="language-link h6 mvn" url={l.slug}>
            {text}
          </Link>
        </i>
      ) : null
    })}
  </div>
)

interface PostViewProps {
  post: Post
  relatedPosts: Post[]
  nextPost: Post | undefined
  previousPost: Post | undefined
  emailUrls: string[]
}

const PostView: React.FC<PostViewProps & ContextProps> = (
  { post, relatedPosts, nextPost, previousPost, emailUrls },
  { minimalView }
) => {
  const postBody = useRef<HTMLDivElement>(null)
  const tracker = useTracking()
  const [showSignUpBanner, setShowSignUpBanner] = useState(() => {
    if (typeof window !== 'undefined') {
      return !window.currentUser
    }
    return false
  })
  const postContainer = useRef(null)
  const { authors, locales = [] } = post
  const getShareDescription = () =>
    post.meta ? post.meta.socialDescription : null

  const getShareMedia = () => {
    if (post.meta && post.meta.pinterestImageUrl) {
      return post.meta.pinterestImageUrl
    }
    if (post.featuredImage && post.featuredImage.url) {
      return post.featuredImage.url
    }
    return null
  }
  const shareProps = post.socialShareUrl
    ? {
        description: getShareDescription(),
        media: getShareMedia(),
        url: post.socialShareUrl,
      }
    : null
  const postSlugsToMarkNoIndex = ['coronavirus-news-and-resources']

  const topicNames = post.meta.topics.map((topic) => topic.name)
  const postId = post.id ? post.id.split('#')[0] : undefined

  useEffect(() => {
    tracker.trackEvent({
      event: track.guideViewed,
      guideTopics:
        post.meta && post.meta.topics && post.meta.topics.length > 0
          ? topicNames
          : null,
      post,
    })

    if (postBody?.current) {
      const trackGuideClicked = () => {
        tracker.trackEvent({
          event: track.guideClicked,
          eventLocation: track.EventLocation.GUIDE,
          postId: postId,
          postType: post.type,
          postRevision: post.revision,
          postSlug: post.slug,
          storeName: 'Babylist',
        })
      }

      const links = Array.from(postBody.current?.getElementsByTagName('a'))
      links.forEach((element: HTMLElement) => {
        element.addEventListener('click', trackGuideClicked)
      })

      return () => {
        links.forEach((element: HTMLElement) =>
          element.removeEventListener('click', trackGuideClicked)
        )
      }
    }
  }, [post])

  const renderShareButtons = () => {
    if (minimalView || !shareProps) return null
    return <ShareButtons className="mbxl" shareText="Share" {...shareProps} />
  }

  const renderAuthors = () => {
    if (!authors) return null
    return authors.map((author) => <Author author={author} key={author.id} />)
  }

  const renderPostLinks = () => {
    if (!previousPost || !nextPost || minimalView) return null
    return (
      <div className="mbxl">
        <div className={css.postLink}>
          <PostLink direction="left" post={previousPost} />
        </div>
        <div className={classnames(css.postLink)}>
          <PostLink direction="right" post={nextPost} />
        </div>
      </div>
    )
  }

  const renderAcquisitionComponents = () => {
    if (minimalView) return null

    const emailType = includes(
      post.meta.topics.map((topic) => topic.slug),
      'pregnancy'
    )
      ? EmailTypes.Pregnancy
      : EmailTypes.Newsletter

    return (
      <>
        <div>
          <EmailCallout
            emailType={emailType}
            emailUrlsByWeek={
              emailType === EMAIL_USER_PREGNANCY
                ? (emailUrls as any)
                : undefined
            }
            nextPost={
              emailType === EMAIL_USER_NEWSLETTER
                ? (nextPost as any)
                : undefined
            }
          />
        </div>

        <div className="related-posts-container-wrapper">
          <div className="container">
            <div className="h3 section-heading text-bold mtn">
              Related Articles
            </div>
            <PostCardRow
              featuredRow
              showTopics
              className="mtl"
              posts={relatedPosts}
            />
          </div>
        </div>

        <SignUpBanner
          show={showSignUpBanner}
          onClose={() => setShowSignUpBanner(false)}
        />
      </>
    )
  }

  if (!post.body) {
    return (
      <div
        style={{ minHeight: '100vh', position: 'relative', marginTop: '-11vh' }}
      >
        <Loader className="center-xy" style={{}} />
      </div>
    )
  }

  return (
    <PostNavBar
      nextPost={nextPost}
      previousPost={previousPost}
      title={post.title}
    >
      <HelloBabyMeta
        articleTags={topicNames}
        canonical={babylistUrl(helloBabyPostPath(post.slug))}
        keywords={topicNames.join(', ')}
        locales={locales}
        noindex={postSlugsToMarkNoIndex.includes(post.slug)}
        seoDescription={post.meta?.seoDescription || ''}
        seoTitle={post.meta?.seoTitle}
        socialDescription={post.meta?.socialDescription}
        socialImage={post.meta?.socialImageUrl || post.featuredImage?.url}
        socialTitle={post.meta?.socialTitle}
        socialUrl={post.meta?.socialUrl}
        title={post.title}
        type="article"
      />
      <div className={css.post}>
        <div ref={postContainer}>
          <div className={css.wrapper}>
            <PostSocialButtons
              {...shareProps}
              container={() => postContainer.current}
            />

            <div className={css.meta}>
              <span className={css.publishedAt}>
                {dateformat(post.publishedAt, 'mmmm d, yyyy')}
              </span>
              <TopicList
                className={classnames(css.topics, 'hidden-xs')}
                topics={post.meta.topics}
              />
              <LanguageSelector locales={post.locales} />
            </div>

            <div className="breakout-right">
              <SectionHeading className={css.title}>
                {post.title}
              </SectionHeading>
              {post.teaser && <h2 className={css.postTeaser}>{post.teaser}</h2>}

              {post.author ? <AuthorByline post={post} /> : null}
            </div>
            <FTCDisclosureTag
              align="left"
              className="mbl"
              disclaimerContext={FTCDisclosureTag.Constants.GUIDE as 'guide'}
            />

            <FeaturedAsset
              assetClasses="mbxl ptl"
              minimalView={minimalView}
              post={post}
            />

            <div className={classnames(css.body)}>
              <div
                className="guides-markdown"
                dangerouslySetInnerHTML={{ __html: post.body }}
                ref={postBody}
              />

              {renderShareButtons()}
              {renderAuthors()}

              <small className="h7 mvxl">
                This information is provided for educational and entertainment
                purposes only. We do not accept any responsibility for any
                liability, loss or risk, personal or otherwise, incurred as a
                consequence, directly or indirectly, from any information or
                advice contained here. Babylist may earn compensation from
                affiliate links in this content. Learn more about{` `}
                <Link url={landingPagePath('editorial-policies-guidelines')}>
                  how we write Babylist content
                </Link>{' '}
                and{` `}
                <Link url={landingPagePath('product-review-processes')}>
                  review products
                </Link>
                , as well as the{' '}
                <Link url={healthAdvisoryBoardPath}>
                  Babylist Health Advisory Board
                </Link>
                .
              </small>
            </div>
          </div>

          {renderPostLinks()}
        </div>
      </div>

      {renderAcquisitionComponents()}
    </PostNavBar>
  )
}

PostView.contextTypes = {
  minimalView: PropTypes.bool,
}

export default PostView
